import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Clock, X, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const AudioPlayer = ({ 
  url, 
  title, 
  duration, 
  onPlay, 
  onAddTimestampedFeedback, 
  timestampedComments = [], 
  onSeekToTimestamp 
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [showTimestampInput, setShowTimestampInput] = useState(false);
  const [timestampFeedback, setTimestampFeedback] = useState('');
  const [selectedTimestamp, setSelectedTimestamp] = useState(null);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        onPlay && onPlay();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleTimelineClick = (e) => {
    const rect = e.target.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const clickedTime = Math.floor(percent * audioDuration);
    
    if (audioRef.current) {
      audioRef.current.currentTime = clickedTime;
    }
    
    setSelectedTimestamp(clickedTime);
    setShowTimestampInput(true);
  };

  const handleSubmitTimestampedFeedback = () => {
    if (timestampFeedback.trim() && selectedTimestamp !== null && onAddTimestampedFeedback) {
      onAddTimestampedFeedback(selectedTimestamp, timestampFeedback);
      setTimestampFeedback('');
      setShowTimestampInput(false);
      setSelectedTimestamp(null);
    }
  };

  const seekToTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
        onPlay && onPlay();
      }
    }
  };

  useEffect(() => {
    if (onSeekToTimestamp && onSeekToTimestamp.setSeek) {
      onSeekToTimestamp.setSeek(seekToTime);
    }
  }, [onSeekToTimestamp]);

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  const timestampMarkers = timestampedComments
    .filter(c => c.timestamp_seconds !== null && c.timestamp_seconds !== undefined)
    .map(c => ({
      position: (c.timestamp_seconds / audioDuration) * 100,
      time: c.timestamp_seconds
    }));

  return (
    <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />
      
      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-transform hover:scale-105"
          style={{ background: '#D4AF37' }}
          data-testid="audio-play-btn"
        >
          {isPlaying ? <Pause size={24} color="#000" /> : <Play size={24} color="#000" className="ml-1" />}
        </button>
        
        <div className="flex-1">
          <p className="font-medium text-sm mb-2" style={{ color: '#ffffff' }}>{title}</p>
          
          <div className="relative">
            <div 
              className="h-3 rounded-full cursor-pointer relative group"
              style={{ background: 'rgba(255,255,255,0.2)' }}
              onClick={handleTimelineClick}
              data-testid="audio-timeline"
            >
              <div 
                className="h-full rounded-full transition-all absolute top-0 left-0"
                style={{ width: `${progress}%`, background: '#D4AF37' }}
              />
              
              {timestampMarkers.map((marker, idx) => (
                <div
                  key={idx}
                  className="absolute top-0 w-1 h-full bg-purple-500 cursor-pointer hover:bg-purple-400 transition-colors"
                  style={{ left: `${marker.position}%`, transform: 'translateX(-50%)' }}
                  onClick={(e) => { e.stopPropagation(); seekToTime(marker.time); }}
                  title={`Feedback at ${formatTime(marker.time)}`}
                />
              ))}
              
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <span className="text-xs px-2 py-1 rounded bg-black/80 text-white whitespace-nowrap">
                  Click to add feedback at this time
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between mt-1 text-xs" style={{ color: '#a3a3a3' }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(audioDuration)}</span>
          </div>
        </div>
      </div>
      
      {showTimestampInput && (
        <div className="mt-4 p-3 rounded-lg animate-in slide-in-from-top-2" style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} style={{ color: '#a855f7' }} />
            <span className="text-sm font-medium" style={{ color: '#a855f7' }}>
              Add feedback at {formatTime(selectedTimestamp)}
            </span>
            <button 
              onClick={() => setShowTimestampInput(false)}
              className="ml-auto p-1 hover:bg-white/10 rounded"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex gap-2">
            <Input
              value={timestampFeedback}
              onChange={(e) => setTimestampFeedback(e.target.value)}
              placeholder="e.g., Transition feels rushed here..."
              className="flex-1 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmitTimestampedFeedback()}
            />
            <Button 
              size="sm"
              onClick={handleSubmitTimestampedFeedback}
              disabled={!timestampFeedback.trim()}
              style={{ background: '#a855f7' }}
            >
              <Send size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
